import { useCallback, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Table, type Column } from '../../components/Table';
import { copyToClipboard, errorMessage, formatDate, formatDateTime, useLoad } from '../members/hooks';
import { SCOPES, scopeLabel, type ApiKey, type ApiKeyWithSecret } from './types';
import { BRAND } from '../../brand';

type Pending = { key: ApiKey; action: 'rotate' | 'revoke' };

function StatusChip({ apiKey }: { apiKey: ApiKey }) {
  switch (apiKey.status) {
    case 'active':
      return <Chip tone="success">Activa</Chip>;
    case 'rotating':
      return <Chip tone="warning">En rotación hasta {formatDateTime(apiKey.graceUntil)}</Chip>;
    case 'expired':
      return <Chip>Reemplazada</Chip>;
    default:
      return <Chip>Revocada</Chip>;
  }
}

export function ApiKeysPage() {
  const { activeOrg } = useSession();
  const orgId = activeOrg?.orgId;
  const loader = useCallback(() => api.get<{ items: ApiKey[] }>('/v1/api-keys', { orgId }).then((r) => r.items), [orgId]);
  const { data, error, loading, reload } = useLoad(loader);

  const [createOpen, setCreateOpen] = useState(false);
  const [secret, setSecret] = useState<ApiKeyWithSecret | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const closeCreate = useCallback(() => setCreateOpen(false), []);
  const closePending = useCallback(() => {
    setPending(null);
    setPendingError(null);
  }, []);
  const closeSecret = useCallback(() => setSecret(null), []);

  async function confirmPending() {
    if (!pending) return;
    setBusy(true);
    setPendingError(null);
    try {
      if (pending.action === 'rotate') {
        const rotated = await api.post<ApiKeyWithSecret>(`/v1/api-keys/${encodeURIComponent(pending.key.id)}/rotate`, undefined, { orgId });
        setSecret(rotated);
        setNotice(`Rotamos ${pending.key.name}. La key anterior sigue funcionando 24 horas.`);
      } else {
        await api.post(`/v1/api-keys/${encodeURIComponent(pending.key.id)}/revoke`, undefined, { orgId });
        setNotice(`Revocamos ${pending.key.name}. Toda solicitud con esa key será rechazada.`);
      }
      setPending(null);
      await reload();
    } catch (err) {
      setPendingError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<ApiKey>[] = [
    { key: 'name', header: 'Nombre', render: (k) => <span className="font-medium">{k.name}</span> },
    { key: 'prefix', header: 'Prefijo', mono: true, render: (k) => `${k.prefix}…` },
    { key: 'scopes', header: 'Permisos', render: (k) => <span className="text-ink-muted">{k.scopes.map(scopeLabel).join(' · ')}</span> },
    { key: 'created', header: 'Creada', render: (k) => formatDate(k.createdAt) },
    { key: 'used', header: 'Último uso', render: (k) => formatDateTime(k.lastUsedAt) },
    { key: 'status', header: 'Estado', render: (k) => <StatusChip apiKey={k} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (k) =>
        k.status === 'active' ? (
          <span className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setPending({ key: k, action: 'rotate' })}>Rotar</Button>
            <Button variant="ghost" size="sm" onClick={() => setPending({ key: k, action: 'revoke' })}>Revocar</Button>
          </span>
        ) : k.status === 'rotating' ? (
          <Button variant="ghost" size="sm" onClick={() => setPending({ key: k, action: 'revoke' })}>Revocar</Button>
        ) : null,
    },
  ];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">API keys</h1>
          <p className="text-sm text-ink-muted">Credenciales para que tus sistemas creen y administren QR.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>Crear API key</Button>
      </div>

      <div aria-live="polite">{notice && <Banner tone="success">{notice}</Banner>}</div>
      {error && <Banner tone="error" action={<Button variant="secondary" size="sm" onClick={() => void reload()}>Reintentar</Button>}>{error}</Banner>}

      <Table
        caption="API keys de la organización"
        columns={columns}
        rows={data ?? []}
        rowKey={(k) => k.id}
        loading={loading && !data}
        empty={<EmptyState title="Sin API keys" description={`Crea una key para conectar tu sistema con ${BRAND}.`} />}
      />
      <p className="text-xs text-ink-muted">
        El secreto se muestra una sola vez al crear o rotar. Si una key supera su límite de solicitudes, la API responde
        “límite excedido”; los QR siguen redirigiendo.
      </p>

      <CreateKeyModal
        open={createOpen}
        orgId={orgId}
        onClose={closeCreate}
        onCreated={async (created) => {
          setCreateOpen(false);
          setSecret(created);
          setNotice(`Creamos la key ${created.name}.`);
          await reload();
        }}
      />

      <Modal
        open={pending !== null}
        onClose={closePending}
        size="sm"
        title={pending?.action === 'rotate' ? `Rotar ${pending.key.name}` : `Revocar ${pending?.key.name ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={closePending}>Cancelar</Button>
            <Button variant={pending?.action === 'revoke' ? 'danger' : 'primary'} loading={busy} onClick={() => void confirmPending()}>
              {pending?.action === 'rotate' ? 'Rotar' : 'Revocar ahora'}
            </Button>
          </>
        }
      >
        {pending?.action === 'rotate' ? (
          <p className="text-sm">Crearemos un secreto nuevo. La key anterior sigue funcionando 24 horas para que actualices la integración sin corte.</p>
        ) : (
          <p className="text-sm">
            Toda solicitud con esta key será rechazada <b>desde este momento</b>. Si una integración la usa, dejará de funcionar. Los QR siguen redirigiendo.
          </p>
        )}
        {pendingError && <Banner tone="error">{pendingError}</Banner>}
      </Modal>

      <SecretModal secret={secret} onClose={closeSecret} />
    </section>
  );
}

function SecretModal({ secret, onClose }: { secret: ApiKeyWithSecret | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const close = useCallback(() => {
    setCopied(false);
    onClose();
  }, [onClose]);
  return (
    <Modal open={secret !== null} onClose={close} title={`Secreto de ${secret?.name ?? ''}`}>
      <Banner tone="warning">Copia el secreto ahora. No lo volveremos a mostrar.</Banner>
      <code className="block break-all rounded-sm bg-mist px-3 py-2 font-mono text-sm" data-testid="api-key-secret">
        {secret?.secret}
      </code>
      <div className="flex justify-end gap-2" aria-live="polite">
        <Button variant="secondary" onClick={async () => setCopied(await copyToClipboard(secret?.secret ?? ''))}>
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
        <Button onClick={close}>Ya lo guardé</Button>
      </div>
    </Modal>
  );
}

function CreateKeyModal({
  open,
  orgId,
  onClose,
  onCreated,
}: {
  open: boolean;
  orgId: string | undefined;
  onClose: () => void;
  onCreated: (key: ApiKeyWithSecret) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['view_qrs']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const close = useCallback(() => {
    setName('');
    setScopes(['view_qrs']);
    setError(null);
    onClose();
  }, [onClose]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) {
      setError('Ponle un nombre de al menos 2 caracteres, por ejemplo “integracion-produccion”.');
      return;
    }
    if (scopes.length === 0) {
      setError('Elige al menos un permiso.');
      return;
    }
    setLoading(true);
    try {
      const created = await api.post<ApiKeyWithSecret>('/v1/api-keys', { name: name.trim(), scopes }, { orgId });
      setName('');
      setScopes(['view_qrs']);
      await onCreated(created);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onClose={close} title="Crear API key">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Input label="Nombre" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        <fieldset className="space-y-1">
          <legend className="text-xs font-medium text-ink-muted">Permisos</legend>
          {SCOPES.map((s) => (
            <label key={s.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scopes.includes(s.value)}
                onChange={(e) => setScopes((prev) => (e.target.checked ? [...prev, s.value] : prev.filter((v) => v !== s.value)))}
              />
              {s.label}
            </label>
          ))}
        </fieldset>
        {error && <Banner tone="error">{error}</Banner>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>Cancelar</Button>
          <Button type="submit" loading={loading}>Crear</Button>
        </div>
      </form>
    </Modal>
  );
}
